import React, { useEffect, useState, useMemo } from "react";
import { Provider } from "react-redux";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { hot, setConfig } from "react-hot-loader";
import store from "./common/store";
import { SnackbarProvider } from "notistack";
import { Helmet } from "react-helmet";
import { arbitrum, mainnet, polygon, optimism, bsc } from "viem/chains";
import { configureChains, createConfig, WagmiConfig } from "wagmi";
import { walletConnectProvider } from "@web3modal/wagmi";
import { alchemyProvider } from "wagmi/providers/alchemy";
import { publicProvider } from "wagmi/providers/public";
import { InjectedConnector } from "wagmi/connectors/injected";
import { MetaMaskConnector } from "wagmi/connectors/metaMask";
import { WalletConnectConnector } from "wagmi/connectors/walletConnect";
import { MaterialDesignContent } from "notistack";
import styled from "@emotion/styled/macro";
import { StylesProvider } from "@mui/styles";
import CustomThemeProvider from "features/home/CustomThemeProvider";
import ConfirmDialogProvider from "features/home/ConfirmDialogProvider";
import ZoomInDialogProvider from "features/home/ZoomInDialogProvider";
import { OpenTrialProvider } from "features/common/TrialDialog/OpenTrialProvider";
import { Notifier } from "features/common";
import { AxiosError, isAxiosError } from "axios";

import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import "react-date-picker/dist/DatePicker.css";
import "react-datepicker/dist/react-datepicker.css";
import "react-calendar/dist/Calendar.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import * as Sentry from "@sentry/react";
import { IntlProvider } from "react-intl";
import { enMessages, zhTWMessages, jaJPMessages } from "locales";
// Import Swiper styles
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/pagination";
import {
  WalletProvider,
  MartianWalletAdapter,
  AptosWalletAdapter,
  RiseWalletAdapter,
  PontemWalletAdapter,
} from "@manahippo/aptos-wallet-adapter";
import {
  ConnectionProvider,
  WalletProvider as SolanaWalletProvider,
} from "@solana/wallet-adapter-react";
import { clusterApiUrl } from "@solana/web3.js";
import { WalletAdapterNetwork } from "@solana/wallet-adapter-base";
import { SolflareWalletAdapter } from "@solana/wallet-adapter-solflare";
import { PhantomWalletAdapter } from "@solana/wallet-adapter-phantom";
import RouteComponent from "./RouteComponent";
import "swiper/css/navigation";
import StateOpenContextProvider from "contexts/stepContext/Open.context";
import CustomSnackbar from "components/Snackbar/SnackbarContent";

const messages = {
  en: enMessages,
  "zh-TW": zhTWMessages,
  jp: jaJPMessages,
};

function shouldIgnoreError(error) {
  const ignoredPatterns = [
    "Socket stalled when trying to connect to",
    "No matching key. pairing:",
    "To use QR modal, please install @walletconnect/modal package",
    "Network Error",
    "No matching key.",
    "Not initialized. engine",
    "CONNECTION ERROR: Couldn't connect to node",
    'Address "undefined" is invalid. Version: viem',
    "wss://relay.walletconnect.org",
    "timeout exceeded",
    "Request aborted",
    "WebSocket connection closed abnormally with code",
    "User rejected the request.",
    'Address "undefined" is invalid. Version: viem',
  ];
  const ignoredNames = ["CanceledError"];

  return (
    ignoredPatterns.some((pattern) => error?.message?.includes(pattern)) ||
    ignoredNames.some((pattern) => error?.name?.includes(pattern))
  );
}

function addAxiosContextRecursive(event, error) {
  if (isAxiosError(error)) {
    addAxiosContext(event, error);
  } else if (error instanceof Error && error.cause) {
    addAxiosContextRecursive(event, error.cause);
  }
}

function addAxiosContext(event, error) {
  if (error.response) {
    const contexts = { ...event.contexts };
    contexts.Axios = { Response: error.response };
    event.contexts = contexts;
  }
}

Sentry.init({
  environment: process.env.REACT_APP_ENV,
  maxBreadcrumbs: 10,
  dsn: "https://1d2b6d6d84ef4aa8944e97f83bcba111@o4504931933093888.ingest.sentry.io/4504972480741376",
  integrations: [
    Sentry.browserTracingIntegration({
      routingInstrumentation: Sentry.reactRouterV6BrowserTracingIntegration(
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      ),
    }),
  ],
  tracesSampleRate: 1.0,
  beforeSend(event, hint) {
    const error = hint?.originalException;
    if (error) {
      addAxiosContextRecursive(event, error);
    }

    if (process.env.REACT_APP_ENV !== "production") return null;
    if (shouldIgnoreError(error)) {
      return null;
    }
    return event;
  },
});

const wallets = [
  new PontemWalletAdapter(),
  new MartianWalletAdapter(),
  new RiseWalletAdapter(),
  new AptosWalletAdapter(),
];

const solanaWallets = [new SolflareWalletAdapter(), new PhantomWalletAdapter()];

const projectId = "e8f69f6a94a0dc82705e2e74954640c7"; //walletConnet project ID

class WorkerManager {
  static instance = null;
  static isStarting = false;

  static async startWorker() {
    if (this.instance || this.isStarting) return this.instance;

    this.isStarting = true;
    try {
      const { worker } = require("mocks/browser.js");
      this.instance = await worker.start();
    } catch (error) {
      console.error("Worker start failed", error);
      this.instance = null;
    } finally {
      this.isStarting = false;
    }

    return this.instance;
  }
}

if (process.env.REACT_APP_ENV === "demo") {
  WorkerManager.startWorker();
}

const { publicClient, chains } = configureChains(
  [mainnet, polygon, optimism, arbitrum, bsc],
  [
    alchemyProvider({
      apiKey: "eg4gCkiDZxQ1X_D4vDk0Mz30lDwWQyVm",
    }),
    walletConnectProvider({ projectId }),

    publicProvider(),
  ]
);
const metadata = {
  name: "MetaCRM",
  description: "MetaCRM Login",
  url: "https://metacrm.inc",
  icons: ["https://avatars.githubusercontent.com/u/37784886"],
};

const connectors = [
  new InjectedConnector({
    chains,
    options: {
      name: "Browser Wallet",
      shimDisconnect: true,
    },
  }),
  new MetaMaskConnector({
    chains,
    options: {
      shimDisconnect: true,
      UNSTABLE_shimOnConnectSelectAccount: true,
    },
  }),
  new WalletConnectConnector({
    chains,
    options: {
      projectId,
      metadata,
    },
  }),
];

const wagmiConfig = createConfig({
  autoConnect: true,
  connectors,
  publicClient,
});

setConfig({
  logLevel: "debug",
});

const RenderMetadata = () => {
  const defaultMetadata = {
    title: "MetaCRM Admin Portal: Your Command Center for Web3 Success",
    desc: "MetaCRM empowers Web3 projects with a suite of enterprise solutions designed to transform community management, data analytics, marketing operations and more. Our intuitive Admin Portal serves as your command center, unifying everything from user analytics to community engagement in one powerful dashboard.",
    url: window.location.href,
    imageURL: window.location.origin + require("assets/img/metadata.png"),
  };

  const metadata = useMemo(() => {
    const meta = { ...defaultMetadata }; // defaultMetadata 是全局的默認元數據
    if (process.env.REACT_APP_ENTITY === "curve") {
      meta.title = "Curve Admin Portal";
      meta.desc = "Powered by MetaCRM";
      meta.imageURL =
        window.location.origin + require("assets/img/curveBanner.png").default;
    } else if (process.env.REACT_APP_ENTITY === "radiant") {
      meta.title = "Radiant Admin Portal";
      meta.desc = "Powered by MetaCRM";
      meta.imageURL =
        window.location.origin +
        require("assets/img/radiantBanner.png").default;
    }
    return meta;
  }, [process.env.REACT_APP_ENTITY]);

  const { title, desc, url, imageURL } = metadata;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="fragment" content="!" />
      <meta name="title" content={title} />
      <meta name="description" content={desc} />
      <meta name="image" content={imageURL} />
      <meta name="url" content={url} />
      <meta itemprop="name" content={title} />
      <meta itemprop="description" content={desc} />
      <meta itemprop="image" content={imageURL} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:image" content={imageURL} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={imageURL} />
    </Helmet>
  );
};

function Root() {
  const [locale, setLocale] = useState("en");
  // const metaDeskWallet = useMetaDeskWalletAdapter();

  // The network can be set to 'devnet', 'testnet', or 'mainnet-beta'.
  const network = WalletAdapterNetwork.Mainnet;

  // You can also provide a custom RPC endpoint.
  const endpoint = useMemo(() => clusterApiUrl(network), [network]);

  // initializePriceCache()
  return (
    <Provider store={store}>
      <RenderMetadata />
      <StylesProvider>
        <CustomThemeProvider>
          {/* react-intl 所定義好的語系檔都在安裝好的套件內，路徑為 node_modules/react-intl/locale-data/ */}
          <IntlProvider
            locale={locale}
            defaultLocale="en"
            messages={messages[locale]}
          >
            <SnackbarProvider
              classes={{ containerRoot: "z-alert" }}
              autoHideDuration={3000}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              iconVariant={{
                info: "",
              }}
            >
              <StateOpenContextProvider>
                <ConfirmDialogProvider>
                  <ZoomInDialogProvider>
                    <WagmiConfig config={wagmiConfig}>
                      <WalletProvider
                        wallets={wallets}
                        onError={(error) => {
                          console.log("Handle Aptos Error Message", error);
                        }}
                      >
                        <ConnectionProvider endpoint={endpoint}>
                          <SolanaWalletProvider
                            wallets={solanaWallets}
                            autoConnect={true}
                            onError={(error) => {
                              console.log("Handle Solana Error Message", error);
                            }}
                          >
                            <OpenTrialProvider>
                              {/* <RouterProvider router={router} /> */}
                              <RouteComponent></RouteComponent>
                              <Notifier />
                            </OpenTrialProvider>
                          </SolanaWalletProvider>
                        </ConnectionProvider>
                      </WalletProvider>
                    </WagmiConfig>
                  </ZoomInDialogProvider>
                </ConfirmDialogProvider>
              </StateOpenContextProvider>
            </SnackbarProvider>
          </IntlProvider>
        </CustomThemeProvider>
      </StylesProvider>
    </Provider>
  );
}

export default hot(module)(Root);
